/* eslint-disable @next/next/no-img-element */
import { CSSProperties, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { DM_Sans, Roboto_Mono } from "next/font/google";
import html2canvas from "html2canvas";

import styles from "./WeeklyTierList.module.css";
import { TIER_COLORS, TIER_ROWS } from "./WeeklyTierListTiers";

import { RootState } from "@/redux/store";
import ModalWrapper from "@/components/shared/ModalWrapper/ModalWrapper";
import { useModalContext } from "@/context/ModalContext";
import SmallProfileImage from "@/components/shared/SmallProfileImage/SmallProfileImage";

const font = Roboto_Mono({ subsets: ["latin"] });
const fontSans = DM_Sans({ weight: "500", subsets: ["latin"] });

type Props = {
  type: "instagram" | "snapchat";
};

// Tier column starts at y=0 inside the chart frame, 50px blocks with 6px
// gaps → 56px row pitch. Avatar / arrow / label vertically align to the
// centre of the user's rank row.
const TIER_ROW_GAP = 6;

/**
 * Periodic tier list share view used to render the IG / Snapchat capture.
 * Mirrors the WeeklyTwoByTwoModal share view structure: flex column with the
 * tier "chart" laid out inside a `position: relative` frame.
 */
export default function WeeklyTierListShareView(props: Props) {
  const { setModalOpen } = useModalContext();
  const imageRef = useRef<HTMLElement>(null);

  const { latestTierList, deliveredForLatest } = useSelector(
    (state: RootState) => state.weeklyTierList
  );
  const userPublicProfile = useSelector(
    (state: RootState) => state.user.publicProfileData
  );
  const userImageUrl = useSelector((state: RootState) => state.user.imageURL);
  const tierBoxSize = useResponsiveTierBoxSize();

  useEffect(() => {
    const handleDownloadImage = async () => {
      if (!imageRef.current) {
        return;
      }
      const canvas = await html2canvas(imageRef.current);
      const data = canvas.toDataURL("image/jpg");

      if (window.Android) {
        window.Android.onHaptic();
        if (props.type === "instagram") {
          window.Android.shareToInstagramQuiz(data);
        } else {
          window.Android.shareToSnapChat(data);
        }
      }
    };

    setTimeout(() => {
      handleDownloadImage();
      setModalOpen(false);
    }, 110);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!latestTierList || !deliveredForLatest) {
    return null;
  }

  const myRank = deliveredForLatest.rank;
  const myRankIndex = Math.max(0, TIER_ROWS.indexOf(myRank));
  const rowTop = myRankIndex * (tierBoxSize + TIER_ROW_GAP);
  const rowMid = rowTop + tierBoxSize / 2;

  return (
    <ModalWrapper title="" noHeader noLine>
      <main ref={imageRef} className={styles.shareMain}>
        <div className={styles.shareLogo}>
          <LogoIconOnly />
        </div>
        <p className={`${styles.shareUrl} ${fontSans.className}`}>
          www.dimensional.me
        </p>

        <h2 className={`${styles.shareTitle} ${font.className}`}>
          {latestTierList.listName.toUpperCase()}
        </h2>

        <div
          className={styles.shareTierChart}
          style={
            {
              "--tier-box-size": `${tierBoxSize}px`,
              "--tier-row-gap": `${TIER_ROW_GAP}px`,
            } as CSSProperties
          }
        >
          <div className={styles.shareTierColumn}>
            {TIER_ROWS.map((rank) => {
              const isMine = rank === myRank;
              return (
                <div
                  key={rank}
                  className={`${styles.shareTierBlock} ${
                    isMine ? "" : styles.shareTierBlockDim
                  }`}
                  style={{ backgroundColor: TIER_COLORS[rank] }}
                >
                  <span className={styles.shareTierLetter}>{rank}</span>
                </div>
              );
            })}
          </div>

          <div
            className={styles.shareArrow}
            style={{ top: `${rowMid - 3}px` }}
            aria-hidden="true"
          >
            <svg
              width="17"
              height="6"
              viewBox="0 0 17 6"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0 3H15.5M15.5 3L13 1M15.5 3L13 5"
                stroke="#FFFFF2"
                strokeWidth="1"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <div className={styles.shareAvatar} style={{ top: `${rowTop}px` }}>
            <SmallProfileImage
              noLink
              medium
              imageUrl={userImageUrl || "/default-image.png"}
              publicProfile={userPublicProfile}
            />
          </div>

          <p
            className={styles.shareYourRank}
            style={{ top: `${rowMid - 8}px` }}
          >
            Your rank: {myRank}
          </p>
        </div>
      </main>
    </ModalWrapper>
  );
}

function useResponsiveTierBoxSize() {
  const [tierBoxSize, setTierBoxSize] = useState(50);

  useEffect(() => {
    const update = () => {
      const responsive = window.innerWidth * 0.145;
      setTierBoxSize(Math.max(50, Math.min(responsive, 64)));
    };

    update();
    window.addEventListener("resize", update);

    return () => {
      window.removeEventListener("resize", update);
    };
  }, []);

  return tierBoxSize;
}

/**
 * Icon-only Dimensional mark (circle + 3 bars + triangle), no wordmark.
 * Paths extracted from `LogoWithText`, viewBox cropped to the icon area so
 * the share view can render the icon above the `www.dimensional.me` URL
 * without doubling up with the "Dimensional" wordmark.
 */
function LogoIconOnly() {
  return (
    <svg
      width="78"
      height="23"
      viewBox="11 0 78 23"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="37.9766"
        width="6.66476"
        height="22.8506"
        rx="3.33238"
        fill="#FFFFF2"
      />
      <rect
        x="46.0352"
        width="6.66476"
        height="22.8506"
        rx="3.33238"
        fill="#FFFFF2"
      />
      <rect
        x="54.1494"
        width="6.66476"
        height="22.8506"
        rx="3.33238"
        fill="#FFFFF2"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M22.4253 22.8506C28.7353 22.8506 33.8506 17.7353 33.8506 11.4253C33.8506 5.11528 28.7353 0 22.4253 0C16.1153 0 11 5.11528 11 11.4253C11 17.7353 16.1153 22.8506 22.4253 22.8506ZM22.3866 13.2767C23.4305 13.2767 24.2767 12.4305 24.2767 11.3866C24.2767 10.3428 23.4305 9.49656 22.3866 9.49656C21.3428 9.49656 20.4966 10.3428 20.4966 11.3866C20.4966 12.4305 21.3428 13.2767 22.3866 13.2767Z"
        fill="#FFFFF2"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M86.0135 22.3125C88.3224 22.3125 89.7558 19.8016 88.5821 17.813L79.2609 2.02031C78.1067 0.0647214 75.2779 0.0647237 74.1236 2.02031L64.8024 17.813C63.6288 19.8016 65.0622 22.3125 67.3711 22.3125H86.0135ZM76.7126 15.4672C77.7834 15.4672 78.6514 14.5991 78.6514 13.5282C78.6514 12.4574 77.7834 11.5892 76.7126 11.5892C75.6419 11.5892 74.7738 12.4574 74.7738 13.5282C74.7738 14.5991 75.6419 15.4672 76.7126 15.4672Z"
        fill="#FFFFF2"
      />
    </svg>
  );
}
